import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './ApplicationListPage.css';

const CATEGORY_LABELS = {
  health: '🏥 Health',
  education: '🎓 Education',
  culture: '🌿 Culture',
  legal: '⚖️ Legal',
  harvesting: '🦌 Harvesting',
};

const PERMIT_KEYWORDS = ['permit', 'license', 'licence', 'regulation'];

const isPermitProgramType = (programType) => {
  const values = Array.isArray(programType) ? programType : (programType ? [programType] : []);
  return values.some((value) => PERMIT_KEYWORDS.some((keyword) => String(value || '').toLowerCase().includes(keyword)));
};

const HealthApplicationListPage = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingDocuments, setExportingDocuments] = useState(false);

  const permitView = searchParams.get('view') === 'permit';
  const defaultPortal = permitView ? 'harvesting' : 'all';

  const canExportApplicants = Boolean(
    currentUser && (
      currentUser.health ||
      currentUser.education ||
      currentUser.harvesting ||
      currentUser.role === 'admin' ||
      currentUser.role === 'staff'
    )
  );

  useEffect(() => {
    if (currentUser && !(currentUser.health || currentUser.education || currentUser.harvesting || currentUser.role === 'admin' || currentUser.role === 'staff')) {
      navigate('/staff-dashboard');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (permitView) {
      setCategoryFilter('harvesting');
      setTypeFilter('all');
      setProgramFilter('all');
    }
  }, [permitView]);

  useEffect(() => {
    fetchApplications();
    fetchPrograms();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/health-program-applications${permitView ? '?portal=harvesting' : ''}`);
      const allApplications = response.data.applications || [];
      const scopedApplications = permitView
        ? allApplications.filter((app) => app.portal === 'harvesting' && isPermitProgramType(app.programType))
        : allApplications.filter((app) => !(app.portal === 'harvesting' && isPermitProgramType(app.programType)));
      setApplications(scopedApplications);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching applications');
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await api.get(`/api/health-programs${permitView ? '?category=harvesting' : ''}`);
      const allPrograms = response.data.programs || [];
      const scopedPrograms = permitView
        ? allPrograms.filter((program) => program.category === 'harvesting' && isPermitProgramType(program.programType))
        : allPrograms.filter((program) => !(program.category === 'harvesting' && isPermitProgramType(program.programType)));
      setPrograms(scopedPrograms);
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted': return 'status-badge status-submitted';
      case 'reviewed': return 'status-badge status-reviewed';
      case 'approved': return 'status-badge status-approved';
      case 'rejected': return 'status-badge status-rejected';
      case 'needsmoreinfo': return 'status-badge status-needsmoreinfo';
      case 'waitlisted': return 'status-badge status-waitlisted';
      default: return 'status-badge';
    }
  };

  const mapStatus = (status) => {
    switch (status) {
      case 'submitted': return 'Submitted';
      case 'reviewed': return 'In Review';
      case 'approved': return 'Approved';
      case 'rejected': return 'Not Approved';
      case 'needsMoreInfo': return 'Needs More Info';
      case 'waitlisted': return 'Waitlisted';
      default: return status;
    }
  };

  const handleDeleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/health-program-applications/${appId}`);
      setApplications(applications.filter(app => app._id !== appId));
      alert('Application deleted successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting application');
    }
  };

  const handleExportApplicants = async () => {
    if (!canExportApplicants) {
      return;
    }

    if (programFilter === 'all') {
      alert('Please select a specific program before exporting applicants.');
      return;
    }

    try {
      setExportingJson(true);

      const query = statusFilter !== 'all' ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const response = await api.get(`/api/health-program-applications/export/program/${programFilter}${query}`);

      const selectedProgram = programs.find((prog) => prog._id === programFilter);
      const safeProgramTitle = (selectedProgram?.title || 'program')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const dateStamp = new Date().toISOString().slice(0, 10);

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${safeProgramTitle || 'program'}-applicants-${dateStamp}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to export applicants');
    } finally {
      setExportingJson(false);
    }
  };

  const parseFilenameFromDisposition = (headerValue, fallbackName) => {
    if (!headerValue) {
      return fallbackName;
    }

    const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const basicMatch = headerValue.match(/filename="?([^";]+)"?/i);
    if (basicMatch?.[1]) {
      return basicMatch[1];
    }

    return fallbackName;
  };

  const handleExportDocuments = async () => {
    if (!canExportApplicants) {
      return;
    }

    if (programFilter === 'all') {
      alert('Please select a specific program before exporting documents.');
      return;
    }

    try {
      setExportingDocuments(true);

      const query = statusFilter !== 'all' ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const response = await api.get(
        `/api/health-program-applications/export/program/${programFilter}/documents${query}`,
        { responseType: 'blob' }
      );

      const selectedProgram = programs.find((prog) => prog._id === programFilter);
      const safeProgramTitle = (selectedProgram?.title || 'program')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const dateStamp = new Date().toISOString().slice(0, 10);
      const fallbackName = `${safeProgramTitle || 'program'}-applicant-documents-${dateStamp}.zip`;
      const contentDisposition = response.headers?.['content-disposition'];
      const downloadName = parseFilenameFromDisposition(contentDisposition, fallbackName);

      const blob = new Blob([response.data], { type: 'application/zip' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = downloadName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to export applicant documents');
    } finally {
      setExportingDocuments(false);
    }
  };

  // Derive available categories and program types from data
  const availableCategories = [...new Set(applications.map(a => a.portal).filter(Boolean))].sort();
  const availableTypes = [...new Set(
    applications.flatMap(a => Array.isArray(a.programType) ? a.programType : (a.programType ? [a.programType] : []))
  )].sort();

  let filteredApplications = applications;

  if (permitView) {
    filteredApplications = filteredApplications.filter((app) => app.portal === 'harvesting' && isPermitProgramType(app.programType));
  }

  if (statusFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app =>
      app.status?.toLowerCase() === statusFilter.toLowerCase()
    );
  }
  if (categoryFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app => app.portal === categoryFilter);
  }
  if (typeFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app =>
      Array.isArray(app.programType)
        ? app.programType.includes(typeFilter)
        : app.programType === typeFilter
    );
  }
  if (programFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app =>
      (app.healthProgramId?._id || app.healthProgramId) === programFilter
    );
  }

  const stats = {
    total: filteredApplications.length,
    submitted: filteredApplications.filter(a => a.status === 'submitted').length,
    reviewed: filteredApplications.filter(a => a.status === 'reviewed').length,
    approved: filteredApplications.filter(a => a.status === 'approved').length,
    rejected: filteredApplications.filter(a => a.status === 'rejected').length,
    needsMoreInfo: filteredApplications.filter(a => a.status === 'needsMoreInfo').length,
    waitlisted: filteredApplications.filter(a => a.status === 'waitlisted').length
  };

  const categoryStats = availableCategories.map(cat => ({
    key: cat,
    label: CATEGORY_LABELS[cat] || cat,
    count: applications.filter(a => a.portal === cat).length
  }));

  return (
    <div className="application-list-container">
      <div className="page-header">
        <h1>{permitView ? '📋 Permit Applications Inbox' : '📋 Program Applications'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate(permitView ? '/staff/harvesting' : '/staff-health-programs')}>
          ← Back to {permitView ? 'Harvesting' : 'Programs'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.submitted}</div>
          <div className="stat-label">Submitted</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.reviewed}</div>
          <div className="stat-label">In Review</div>
        </div>
        <div className="stat-card stat-approved">
          <div className="stat-number">{stats.approved}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card stat-rejected">
          <div className="stat-number">{stats.rejected}</div>
          <div className="stat-label">Not Approved</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.waitlisted}</div>
          <div className="stat-label">Waitlisted</div>
        </div>
      </div>

      {!permitView && categoryStats.length > 0 && (
        <div className="category-tabs">
          <button
            className={`category-tab${categoryFilter === defaultPortal ? ' active' : ''}`}
            onClick={() => setCategoryFilter(defaultPortal)}
          >
            All Categories <span className="tab-count">{applications.length}</span>
          </button>
          {categoryStats.map(cat => (
            <button
              key={cat.key}
              className={`category-tab${categoryFilter === cat.key ? ' active' : ''}`}
              onClick={() => setCategoryFilter(cat.key)}
            >
              {cat.label} <span className="tab-count">{cat.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="filter-controls">
        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="reviewed">In Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Not Approved</option>
            <option value="needsMoreInfo">Needs More Info</option>
            <option value="waitlisted">Waitlisted</option>
          </select>
        </div>

        {availableTypes.length > 0 && (
          <div className="filter-group">
            <label htmlFor="type-filter">Category Type:</label>
            <select
              id="type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">{permitView ? 'All Permit Types' : 'All Types'}</option>
              {availableTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label htmlFor="program-filter">Program:</label>
          <select
            id="program-filter"
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
          >
            <option value="all">All Programs</option>
            {programs.map(prog => (
              <option key={prog._id} value={prog._id}>{prog.title}</option>
            ))}
          </select>
        </div>

        {canExportApplicants && (
          <div className="filter-group export-group">
            <div className="export-actions">
              <button
                type="button"
                className="btn btn-export"
                onClick={handleExportApplicants}
                disabled={exportingJson || programFilter === 'all'}
                title={programFilter === 'all' ? 'Select a program to enable export' : 'Export applicants JSON for selected program'}
              >
                {exportingJson ? 'Exporting...' : 'Export JSON'}
              </button>
              <button
                type="button"
                className="btn btn-export-documents"
                onClick={handleExportDocuments}
                disabled={exportingDocuments || programFilter === 'all'}
                title={programFilter === 'all' ? 'Select a program to enable export' : 'Export all applicant documents for selected program'}
              >
                {exportingDocuments ? 'Exporting...' : 'Export Documents'}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {loading ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading applications...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-state">
          <p>No applications found.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="applications-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Category</th>
                <th>Program</th>
                <th>Program Category</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-results">No applications match the selected filters</td>
                </tr>
              ) : (
                filteredApplications.map(app => (
                  <tr key={app._id}>
                    <td>{app.applicantName}</td>
                    <td>
                      <span className={`portal-badge portal-${app.portal}`}>
                        {CATEGORY_LABELS[app.portal] || app.portal || '—'}
                      </span>
                    </td>
                    <td>{app.programTitle || 'N/A'}</td>
                    <td>
                      {Array.isArray(app.programType)
                        ? app.programType.join(', ')
                        : (app.programType || '—')}
                    </td>
                    <td>{formatDate(app.dateSubmitted)}</td>
                    <td>
                      <span className={getStatusBadgeClass(app.status)}>
                        {mapStatus(app.status)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-cell">
                        <button 
                          className="table-action-btn btn-review" 
                          onClick={() => navigate(`/review-health-application/${app._id}`)}
                        >
                          Review
                        </button>
                        <button 
                          className="table-action-btn btn-delete" 
                          onClick={() => handleDeleteApplication(app._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HealthApplicationListPage;
